import metadata from "libphonenumber-js/metadata.max.json";
import {
  getCountryCallingCode,
  isSupportedCountry,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from "libphonenumber-js/max";
import {
  ISO2_TO_ISO3_PHONE_COUNTRY_CODES,
  ISO3_TO_ISO2_PHONE_COUNTRY_CODES,
} from "../shared/phoneCountryCodeMap";

const MYINVOIS_E164_REGEX = /^\+[1-9]\d{1,18}$/;

const normalizePhoneToken = (value) =>
  value !== undefined && value !== null ? String(value).trim() : "";

const normalizeToPhoneCountryCode = (countryCode) => {
  const normalizedCountryCode = normalizePhoneToken(countryCode).toUpperCase();
  if (!normalizedCountryCode) return null;

  if (normalizedCountryCode.length === 2 && isSupportedCountry(normalizedCountryCode)) {
    return normalizedCountryCode;
  }

  const mappedIso2Code = ISO3_TO_ISO2_PHONE_COUNTRY_CODES[normalizedCountryCode];
  return mappedIso2Code && isSupportedCountry(mappedIso2Code) ? mappedIso2Code : null;
};

const getPhoneCountryDialCode = (countryCode) => {
  const normalizedCountryCode = normalizePhoneToken(countryCode);
  if (/^\+\d+$/.test(normalizedCountryCode)) {
    return normalizedCountryCode;
  }

  const normalizedPhoneCountryCode = normalizeToPhoneCountryCode(countryCode);
  if (!normalizedPhoneCountryCode) return null;

  try {
    return `+${getCountryCallingCode(normalizedPhoneCountryCode)}`;
  } catch {
    return null;
  }
};

const isPhoneCountrySupported = (countryCode) => Boolean(normalizeToPhoneCountryCode(countryCode));

const getPhoneNumberLengthLimits = (countryCode) => {
  const normalizedPhoneCountryCode = normalizeToPhoneCountryCode(countryCode);
  if (!normalizedPhoneCountryCode) {
    return { min: 1, max: 20 };
  }

  const rawMetadata = metadata?.countries?.[normalizedPhoneCountryCode];
  const possibleLengths = Array.isArray(rawMetadata?.[3])
    ? rawMetadata[3].filter((value) => Number.isInteger(value))
    : [];

  if (!possibleLengths.length) {
    return { min: 1, max: 20 };
  }

  return {
    min: Math.min(...possibleLengths),
    max: Math.max(...possibleLengths),
  };
};

const buildInternationalCandidate = (value, countryCode) => {
  const normalizedValue = normalizePhoneToken(value);
  if (!normalizedValue) return "";
  if (normalizedValue.startsWith("+")) return normalizedValue;

  const dialCode = getPhoneCountryDialCode(countryCode);
  if (!dialCode) return null;

  const digitsOnly = normalizedValue.replace(/\D/g, "");
  const dialDigits = dialCode.replace(/\D/g, "");
  if (!digitsOnly || !dialDigits) return null;

  const localDigits = digitsOnly.startsWith(dialDigits)
    ? digitsOnly.slice(dialDigits.length)
    : digitsOnly.replace(/^0+/, "");

  if (!localDigits) return null;
  return `+${dialDigits}${localDigits}`;
};

const normalizeContactNumberToE164 = (value, countryCode) => {
  const normalizedValue = normalizePhoneToken(value);
  if (!normalizedValue) return "";

  const candidateValue = buildInternationalCandidate(normalizedValue, countryCode);
  if (!candidateValue) return null;

  const parsedPhoneNumber = parsePhoneNumberFromString(candidateValue);
  if (!parsedPhoneNumber?.isValid()) return null;

  return MYINVOIS_E164_REGEX.test(parsedPhoneNumber.number) ? parsedPhoneNumber.number : null;
};

const splitStoredContactNumber = (value, fallbackCountryCode = "") => {
  const normalizedValue = normalizePhoneToken(value);
  const fallbackValue = normalizePhoneToken(fallbackCountryCode).toUpperCase();

  if (!normalizedValue) {
    return {
      countryCode: fallbackValue,
      localNumber: "",
      normalizedNumber: "",
    };
  }

  if (!normalizedValue.startsWith("+")) {
    return {
      countryCode: fallbackValue,
      localNumber: normalizedValue,
      normalizedNumber: normalizedValue,
    };
  }

  const parsedPhoneNumber = parsePhoneNumberFromString(normalizedValue);
  if (!parsedPhoneNumber?.isValid()) {
    return {
      countryCode: fallbackValue,
      localNumber: normalizedValue,
      normalizedNumber: normalizedValue,
    };
  }

  return {
    countryCode: ISO2_TO_ISO3_PHONE_COUNTRY_CODES[parsedPhoneNumber.country] || fallbackValue,
    localNumber: parsedPhoneNumber.nationalNumber || "",
    normalizedNumber: parsedPhoneNumber.number,
  };
};

const validateContactNumberByCountry = (value, countryCode, fieldLabel = "Contact number") => {
  const normalizedValue = normalizePhoneToken(value);
  if (!normalizedValue) return `${fieldLabel} is required`;

  if (!/^\d+$/.test(normalizedValue) && !normalizedValue.startsWith("+")) {
    return `${fieldLabel} should only contain numbers`;
  }

  const normalizedPhoneCountryCode = normalizeToPhoneCountryCode(countryCode);
  if (!normalizedValue.startsWith("+") && normalizedPhoneCountryCode) {
    const lengthValidation = validatePhoneNumberLength(normalizedValue, normalizedPhoneCountryCode);
    const { min, max } = getPhoneNumberLengthLimits(countryCode);

    if (lengthValidation === "TOO_SHORT") {
      return `${fieldLabel} must be at least ${min} digits for the selected country`;
    }
    if (lengthValidation === "TOO_LONG") {
      return `${fieldLabel} must not exceed ${max} digits for the selected country`;
    }
  }

  const normalizedE164 = normalizeContactNumberToE164(normalizedValue, countryCode);
  if (!normalizedE164) {
    return `${fieldLabel} is not valid for the selected country`;
  }
  if (normalizedE164.length > 20) {
    return `${fieldLabel} must not exceed 20 characters`;
  }

  return null;
};

const buildPhoneCountryCodeOptions = (countryOptions = []) =>
  countryOptions
    .filter((countryOption) => isPhoneCountrySupported(countryOption.value))
    .map((countryOption) => {
      const dialCode = getPhoneCountryDialCode(countryOption.value);
      const dialCodeSuffix = dialCode ? ` (${dialCode})` : "";
      return {
        value: countryOption.value,
        label: `${countryOption.label}${dialCodeSuffix}`,
        searchLabel: `${countryOption.label} ${dialCode || ""}`.trim(),
      };
    });

export {
  buildPhoneCountryCodeOptions,
  getPhoneCountryDialCode,
  getPhoneNumberLengthLimits,
  isPhoneCountrySupported,
  normalizeContactNumberToE164,
  splitStoredContactNumber,
  validateContactNumberByCountry,
};
